// src/components/auth/SignIn.tsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logger from '../../utils/logger';

// --- Firebase Imports ---
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { firebaseAuth } from '../../config/firebase'; // Your initialized Firebase Auth instance

const SignInFirebaseWithEmailPassword: React.FC = () => {

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null); // For displaying Firebase errors
    const [loading, setLoading] = useState(false); // Optional: for loading state
    const navigate = useNavigate();
    const location = useLocation();

    // --- Get Redirect Path ---
    const getRedirectPath = () => {
        return (location.state as any)?.from?.pathname || '/';
    };

    // --- Firebase Email/Password Sign In Handler ---
    const handleEmailPasswordSignIn = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);
        setLoading(true);
        try {
            logger.info(`Attempting Firebase email/password sign-in for ${email}`);
            await signInWithEmailAndPassword(firebaseAuth, email, password);
            logger.info(`Firebase email/password sign-in successful for ${email}.`);
            // onAuthStateChanged in AuthContext handles setting the user state.
            navigate(getRedirectPath(), { replace: true });

        } catch (err: any) {
            let errorMessage = err.message;

            logger.debug( err);
            logger.debug( errorMessage);

            // strip the "Firebase: " in the errors if present

            if (errorMessage.startsWith("Firebase: ")) {
                errorMessage = errorMessage.slice("Firebase: ".length);
            }

            if (errorMessage === "Error (auth/invalid-credential).") {
                logger.warn(" Invalid creds");
                errorMessage = "Unable to sign in with provided credentials. Please check your email and password as well as signin method.";
                // ToDo: tailor error message if user is using @gmail.com accounts
            } else if (errorMessage === "Error (auth/network-request-failed).") {
                logger.warn(" Network is missing out");
                errorMessage = "Our authentication network is down. Please check back at a later time.";
            } else {
                logger.warn("Email/password sign-in error:", err);
            }

            // Other errors to handle:
            // Firebase: Error (auth/email-already-in-use)
            // Firebase: Error (auth/invalid-email)
            // Firebase: Error (auth/operation-not-allowed)
            // Firebase: Error (auth/user-disabled)
            // Firebase: Error (auth/user-not-found)
            // Firebase: Error (auth/wrong-password)

            setError(errorMessage || 'Failed to sign in. Check email and password.');
        } finally {
            setLoading(false);
        }            
    };

    return (
        <div className="auth-direct-signup-form">
        {/* --- Firebase Email/Password Form --- */}
        <form onSubmit={handleEmailPasswordSignIn} className="space-y-4">
            <div>
                <label htmlFor="email-firebase" className="auth-label">Email</label>
                <input
                    type="email"
                    id="email-firebase" // Unique ID
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="auth-input"
                    placeholder="you@example.com"
                />
            </div>
            <div>
                <label htmlFor="password-firebase" className="auth-label">Password</label>
                <input
                    type="password"
                    id="password-firebase" // Unique ID
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="auth-input"
                    placeholder="********"
                />
            </div>
            <button type="submit" className="deriq-action-button w-full" disabled={loading}>
                {loading ? 'Signing In...' : 'Sign In with Email'}
            </button>
        </form>
        {/* --- Display Errors --- */}
        {error && (
            <p className="auth-error-message">{error}</p>
        )}
        </div>
    )
}

// --- SignInFirebase Component (Filled In) ---
const SignInFirebase: React.FC = () => {
    // Note: We don't need useAuth().signIn here, Firebase handles it.
    const navigate = useNavigate();
    const location = useLocation();

    const [error, setError] = useState<string | null>(null); // For displaying Firebase errors
    const [loading, setLoading] = useState(false); // Optional: for loading state
    const [showEmailPasswordForm, setShowEmailPasswordForm] = useState(false);   

    // --- Get Redirect Path ---
    const getRedirectPath = () => {
        return (location.state as any)?.from?.pathname || '/';
    };

    // --- Firebase Google Sign In Handler ---
    const handleFirebaseGoogleSignIn = async () => {
        setError(null);
        setLoading(true);
        const provider = new GoogleAuthProvider();
        try {
            logger.info("Attempting Firebase Google sign-in popup.");
            await signInWithPopup(firebaseAuth, provider);
            logger.info("Firebase Google sign-in successful.");
            // onAuthStateChanged handles setting user state.
            navigate(getRedirectPath(), { replace: true });
        } catch (err: any) {
            logger.error("Firebase Google sign-in error:", err);
            // Handle specific errors like popup closed by user if necessary
            if (err.code === 'auth/popup-closed-by-user') {
                setError('Sign-in cancelled.');
            } else {
                setError(err.message || 'Failed to sign in with Google.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Simple Google Icon for Firebase button
    const GoogleIcon = () => <svg viewBox="0 0 48 48" height="1em" width="1em"><path fill="#EA4335" d="M24 9.5c3.4 0 6.3 1.2 8.6 3.3l6.4-6.4C34.6 2.7 29.7 1 24 1 14.9 1 7.2 6.4 3.6 14l7.7 6C13.1 13.4 18.1 9.5 24 9.5z"></path><path fill="#4285F4" d="M46.2 25.1c0-1.6-.1-3.2-.4-4.7H24v9h12.5c-.6 2.9-2.2 5.4-4.7 7.1l7.3 5.7c4.3-4 6.9-9.8 6.9-16.7z"></path><path fill="#34A853" d="M11.3 28.1c-.4-1.2-.6-2.5-.6-3.8s.2-2.6.6-3.8l-7.7-6C1.6 18.1 0 22 0 24.3c0 2.3.6 6.2 3.6 9.8l7.7-6z"></path><path fill="#FBBC05" d="M24 47.3c5.7 0 10.6-1.9 14.1-5.2l-7.3-5.7c-1.9 1.3-4.3 2.1-7.1 2.1-5.9 0-11-3.9-12.9-9.2l-7.7 6C7.2 41 14.9 47.3 24 47.3z"></path><path fill="none" d="M0 0h48v48H0z"></path></svg>;

    return (
        // The auth-form-card in SignIn.tsx provides the main padding.
        // This div arranges the content within that card.
        <div className="flex flex-col w-full items-center space-y-4">
            <h2 className="signin-firebase-title">Sign In</h2> {/* Or use auth-title if style is identical */}

            {/* --- Firebase Google Sign In Button --- */}
            <button
                onClick={handleFirebaseGoogleSignIn}
                className="deriq-action-button max-w-xs" // Match form width
                disabled={loading}
                type="button" // Important: prevent form submission if inside a form
            >
                <GoogleIcon />
                <span>{loading ? 'Connecting...' : 'Sign In with Google'}</span>
            </button>

            <div className="flex items-center w-full max-w-xs my-4">
                <hr className="flex-grow border-gray-600"/>
                <span className="px-2 text-gray-400 text-sm">OR</span>
                <hr className="flex-grow border-gray-600"/>
            </div>

           {showEmailPasswordForm ? (
                <SignInFirebaseWithEmailPassword />
            ) : (
                <button
                    onClick={() => setShowEmailPasswordForm(true)}
                    className="deriq-action-button max-w-xs" // Match form width
                    type="button"
                >Sign In with Email</button>
            )}

            {/* --- Display Errors --- */}
            {error && (
                <p className="auth-error-message">{error}</p> // Use shared error style
            )}

             {/* Optional: Link to Sign Up */}
             <p className="auth-link-text">
                Don't have an account?{'  '}
                <button onClick={() => navigate('/signup')} className="auth-nav-link">
                    Sign Up
                </button>
            </p>
        </div>
    );
}

export default SignInFirebase;
